/* eslint-disable sort-keys-fix/sort-keys-fix */
/* eslint-disable import/order */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Components
import { Autocomplete, Box, Chip, Skeleton, TextField } from '@mui/material';
import { MDropdown } from 'common';
import renderInput from 'feature/inventory-management/reconsignments/edit/renderUtils';

// Utils
import { capitalize } from 'utils/helpers/format';
import { userRoles } from 'utils/currentUserRoles';
import { useGetAffiliateOptions } from 'hooks';
import {
  getSingleFilteredOption,
  handleSingleOptionEnterToggle,
} from 'feature/loading-management/utils/autocompleteEnterToggle';
import {
  useGetAuthSeqFilterLocationsListQuery,
  useGetAuthSeqFilterProductsListQuery,
} from 'feature/loading-management/authorization-sequencing/authorizationSequencingEndpoints';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import { ANY_VALUE, isAuthSequencingUpdatesEnabled } from '../utils/const';

// Styles
import { AuthContext } from 'authentication/AuthContext';

const renderCollapsedTags = selectedOptions => {
  if (!selectedOptions.length) {
    return null;
  }

  const firstLabel = selectedOptions[0]?.label ?? selectedOptions[0]?.value ?? '';

  return (
    <>
      <span
        style={{
          display: 'inline-block',
          marginLeft: 5,
          marginRight: 8,
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {firstLabel}
      </span>
      {selectedOptions.length > 1 && (
        <Chip label={`+${selectedOptions.length - 1}`} size="small" variant="autocomplete" />
      )}
    </>
  );
};

const multiSelectCompactSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 48,
  },
  width: '100%',
};

const normalizeAuthSeqFilterOptions = options =>
  (Array.isArray(options) ? options : [])
    .map(option => {
      const label = option?.nameLong;
      const value = option?.locationId ?? option?.productId ?? option?.nameLong;
      return label != null && value != null ? { label, value } : null;
    })
    .filter(Boolean);

const isAuthSeqOptionEqual = (option, selected) =>
  `${option?.value ?? ''}` === `${selected?.value ?? ''}`;

const Filters = ({
  loading,
  onChangeViewBy,
  relatedIds,
  searchTerm,
  selectedIndex,
  setFilters,
  viewByOptions,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAffiliateId, dataCompanyId, viewBy } = useParams();
  const { pathname } = useLocation();
  const { affiliates, currentAffiliate, setCurrentSelectedAffiliate } = useContext(AuthContext);
  const authorizationType = pathname.split('/')[5];
  const isAuthorizationSequencing = authorizationType === 'authorization-sequencing';
  // Feature flag:REACT_APP_LD_SEQUENCING_AUTH_LIST
  const isAuthSequencingUpdatesFlagOn =
    isAuthorizationSequencing && isAuthSequencingUpdatesEnabled();
  const [relatedEntityInputValue, setRelatedEntityInputValue] = useState('');
  const relatedEntityLabel = viewBy === 'product' ? 'Locations' : 'Products';
  const supplierCompanyCustomerIdName = currentAffiliate?.customerIdName || currentAffiliateId;
  const supplierDataCompanyId = currentAffiliate?.dataCompanyId || dataCompanyId;
  const supplierFilterParams = useMemo(
    () => ({
      supplier: {
        companyCustomerIdName: supplierCompanyCustomerIdName,
        dataCompanyId: supplierDataCompanyId,
      },
    }),
    [supplierCompanyCustomerIdName, supplierDataCompanyId]
  );
  const hasSupplierParams =
    supplierFilterParams.supplier.companyCustomerIdName &&
    supplierFilterParams.supplier.dataCompanyId;
  const shouldFetchFilterProductsList =
    isAuthSequencingUpdatesFlagOn && viewBy === 'location' && hasSupplierParams;
  const shouldFetchFilterLocationsList =
    isAuthSequencingUpdatesFlagOn && viewBy === 'product' && hasSupplierParams;
  const {
    data: filterProductsListQuery = [],
    isFetching: isFetchingFilterProducts,
    isLoading: isLoadingFilterProducts,
  } = useGetAuthSeqFilterProductsListQuery(supplierFilterParams, {
    skip: !shouldFetchFilterProductsList,
  });
  const {
    data: filterLocationsListQuery = [],
    isFetching: isFetchingFilterLocations,
    isLoading: isLoadingFilterLocations,
  } = useGetAuthSeqFilterLocationsListQuery(supplierFilterParams, {
    skip: !shouldFetchFilterLocationsList,
  });
  const endpointOptions = useMemo(() => {
    if (viewBy === 'location') return normalizeAuthSeqFilterOptions(filterProductsListQuery);
    if (viewBy === 'product') return normalizeAuthSeqFilterOptions(filterLocationsListQuery);
    return [];
  }, [filterLocationsListQuery, filterProductsListQuery, viewBy]);
  const isRelatedOptionsLoading =
    (shouldFetchFilterProductsList && (isFetchingFilterProducts || isLoadingFilterProducts)) ||
    (shouldFetchFilterLocationsList && (isFetchingFilterLocations || isLoadingFilterLocations));
  const multiSelectOptions = endpointOptions.filter(({ value }) => value !== ANY_VALUE);
  const showNoRelatedOptionsTooltip =
    shouldFetchFilterProductsList || shouldFetchFilterLocationsList;
  const selectedMultiSelectValues = relatedIds
    .map(value => multiSelectOptions.find(option => isAuthSeqOptionEqual(option, { value })))
    .filter(Boolean);
  const relatedSingleFilteredOption = useMemo(
    () =>
      getSingleFilteredOption(
        multiSelectOptions,
        relatedEntityInputValue,
        option => option?.label ?? ''
      ),
    [multiSelectOptions, relatedEntityInputValue]
  );

  // based on where the user is determines
  // the array of roles passed to the fun
  // that returns the proper affiliates
  // for the dropdown
  const roles = () => {
    const isSelfAuth = authorizationType === 'self-authorizations';
    const isSupplierAuth = authorizationType === 'supplier-authorizations';
    let arr;

    if (isSelfAuth || isSupplierAuth) {
      arr = [userRoles.SUPPLIER, userRoles.INVENTORY_OWNER];
    } else {
      arr = [userRoles.SUPPLIER];
    }

    return arr;
  };

  const affiliatesOptions = useGetAffiliateOptions(roles());

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    navigate(
      `/affiliate/${affiliate.customerIdName}/${affiliate.dataCompanyId}/loading-management/${authorizationType}/${viewBy}`,
      {
        replace: true,
      }
    );
  };

  // Sync AuthContext affiliate and dependent filter state from the URL.
  // This only runs after navigation actually completes (user confirmed the
  // unsaved-changes dialog), so cancelling leaves all current state intact.
  useEffect(() => {
    if (!affiliates.length || !currentAffiliateId || !dataCompanyId) return;
    const idx = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        customerIdName === currentAffiliateId && id.toString() === dataCompanyId.toString()
    );
    if (idx < 0) return;
    const urlAffiliate = affiliates[idx];
    const affiliateChanged =
      currentAffiliate?.customerIdName !== urlAffiliate.customerIdName ||
      currentAffiliate?.dataCompanyId !== urlAffiliate.dataCompanyId;
    if (!affiliateChanged) return;
    setCurrentSelectedAffiliate(idx);
    setFilters({ name: 'searchTerm', value: undefined });
    if (isAuthSequencingUpdatesFlagOn) {
      setFilters({ name: 'relatedIds', value: [] });
      setRelatedEntityInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliateId, dataCompanyId, affiliates]);

  const handleChangeViewBy = value => {
    onChangeViewBy();
    // update URL from change
    navigate(
      `/affiliate/${currentAffiliateId}/${dataCompanyId}/loading-management/${authorizationType}/${value.toLowerCase()}/`,
      {
        replace: true,
      }
    );
  };

  const handleFilterChange = value => {
    setFilters({ name: 'searchTerm', value });
    if (!isAuthorizationSequencing && selectedIndex >= 0) {
      setFilters({ name: 'selectedIndex', value: undefined });
    }
  };

  const withUnsavedGuard = nextAction => {
    const hasUnsavedChanges =
      typeof window !== 'undefined' && Boolean(window.__authSeqHasUnsavedChanges);

    if (!hasUnsavedChanges) {
      nextAction();
      return;
    }

    dispatch(
      openConfirmationDialog({
        cancelAction: () => {},
        confirmAction: () => {
          nextAction();
        },
        header: 'Unsaved Changes',
        leftButtonActionType: 'confirm',
        leftButtonText: 'Continue',
        message:
          'Are you sure you want to leave this page?  You have unsaved Authorization Sequencing changes.',
        params: {},
        rightButtonActionType: 'cancel',
        rightButtonText: 'Keep Editing',
      })
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        bgcolor: { xs: 'surface.mobile', md: 'surface.white' },
        borderBottom: { xs: 'none', lg: `1px solid rgba(0, 0, 0, 0.12)` },
        px: { xs: 0, lg: 2 },
        py: { xs: 0, lg: 3 },
        zIndex: { lg: 10 },
      }}
      width={1}
    >
      {loading ? (
        <>
          <Box mb={2}>
            <Skeleton borderradius={4} height={47} variant="rectangular" width="100%" />
          </Box>
          <Box mb={2}>
            <Skeleton borderradius={4} height={47} variant="rectangular" width="100%" />
          </Box>
          <Box>
            <Skeleton borderradius={4} height={47} variant="rectangular" width="100%" />
          </Box>
        </>
      ) : (
        <>
          {affiliatesOptions.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <MDropdown
                id="affiliate-select"
                items={affiliatesOptions}
                label="Affiliate"
                name="affiliate"
                onChange={({ value }) => handleAffiliateChange(value)}
                value={
                  currentAffiliate
                    ? `${currentAffiliate.customerIdName}-${currentAffiliate.dataCompanyId}`
                    : ''
                }
              />
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <MDropdown
              id="viewBy-select"
              items={viewByOptions}
              label="View By"
              name="viewBy"
              onChange={({ value }) => handleChangeViewBy(value)}
              value={viewBy}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={
                viewBy
                  ? isAuthSequencingUpdatesFlagOn
                    ? `Search ${capitalize(viewBy)}s`
                    : `Filter ${capitalize(viewBy)}s SCAC or Name`
                  : ''
              }
              onChange={e => handleFilterChange(e.target.value)}
              placeholder={isAuthSequencingUpdatesFlagOn ? 'Search' : 'Filter'}
              value={searchTerm}
            />
          </Box>

          {isAuthSequencingUpdatesFlagOn && (
            <Box>
              <Autocomplete
                autoHighlight
                disableCloseOnSelect
                disablePortal
                getOptionLabel={option => option.label}
                inputValue={relatedEntityInputValue}
                isOptionEqualToValue={isAuthSeqOptionEqual}
                multiple
                onChange={(_, values) => {
                  withUnsavedGuard(() => {
                    setFilters({
                      name: 'relatedIds',
                      value: values.map(({ value }) => value),
                    });
                    setRelatedEntityInputValue('');
                  });
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setRelatedEntityInputValue(newInputValue);
                  }
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !relatedEntityInputValue.trim()) {
                    Object.assign(event, { defaultMuiPrevented: true });
                    event.preventDefault();
                    event.stopPropagation();

                    if (selectedMultiSelectValues.length === 1) {
                      withUnsavedGuard(() => {
                        setFilters({
                          name: 'relatedIds',
                          value: [],
                        });
                      });
                    } else if (selectedMultiSelectValues.length > 1) {
                      withUnsavedGuard(() => {
                        setFilters({
                          name: 'relatedIds',
                          value: selectedMultiSelectValues
                            .slice(0, selectedMultiSelectValues.length - 1)
                            .map(({ value }) => value),
                        });
                      });
                    }
                    return;
                  }

                  handleSingleOptionEnterToggle(
                    event,
                    relatedSingleFilteredOption,
                    selectedMultiSelectValues,
                    values => {
                      withUnsavedGuard(() => {
                        setFilters({
                          name: 'relatedIds',
                          value: values.map(({ value }) => value),
                        });
                        setRelatedEntityInputValue('');
                      });
                    },
                    isAuthSeqOptionEqual
                  );
                }}
                openOnFocus
                options={multiSelectOptions}
                renderInput={renderInput({
                  label: relatedEntityLabel,
                  length: multiSelectOptions.length,
                  loading: isRelatedOptionsLoading,
                  selectedCount: selectedMultiSelectValues.length,
                  showNoOptions: showNoRelatedOptionsTooltip,
                })}
                renderTags={renderCollapsedTags}
                slotProps={{
                  listbox: {
                    onWheel: e => e.stopPropagation(),
                    onTouchMove: e => e.stopPropagation(),
                  },
                }}
                sx={multiSelectCompactSx}
                value={selectedMultiSelectValues}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
// taylor - test navigate and other hooks still work
export default Filters;

Filters.propTypes = {
  loading: bool.isRequired,
  onChangeViewBy: func,
  relatedIds: arrayOf(oneOfType([string, number])),
  searchTerm: string,
  selectedIndex: number,
  setFilters: func.isRequired,
  viewByOptions: arrayOf(shape({})).isRequired,
};

Filters.defaultProps = {
  onChangeViewBy: () => {},
  relatedIds: [],
  searchTerm: '',
  selectedIndex: undefined,
};
